import { Box } from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useColorMode } from '../colorMode';

// Custom segmented toggle — pill with a sliding thumb, sun/moon icons either
// side. Feels more intentional than a plain IconButton and is unambiguous
// about what state you're in.
export function ColorModeToggle() {
  const { mode, toggle } = useColorMode();
  const isDark = mode === 'dark';

  return (
    <Box
      role="switch"
      aria-checked={isDark}
      aria-label="Toggle colour mode"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      sx={(t) => ({
        position: 'relative',
        // Slightly narrower on phones to reclaim toolbar width; desktop keeps 62.
        width: { xs: 56, sm: 62 },
        height: 30,
        flexShrink: 0,
        borderRadius: 999,
        border: `1px solid ${t.palette.divider}`,
        backgroundColor: isDark ? 'rgba(255,204,0,0.08)' : 'rgba(0,0,0,0.04)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '6px',
        transition: 'background-color 200ms ease, border-color 200ms ease',
        outline: 'none',
        '&:hover': { borderColor: t.palette.text.secondary },
        '&:focus-visible': { boxShadow: `0 0 0 2px ${t.palette.primary.main}` },
      })}
    >
      {/* Sliding thumb */}
      <Box
        sx={(t) => ({
          position: 'absolute',
          top: 2,
          left: isDark ? 'calc(100% - 28px)' : 2,
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: t.palette.primary.main,
          transition: 'left 220ms cubic-bezier(.2,.8,.2,1)',
          boxShadow: isDark
            ? '0 1px 3px rgba(0,0,0,0.6)'
            : '0 1px 3px rgba(0,0,0,0.2)',
          zIndex: 1,
        })}
      />
      {/* Sun (light mode end) */}
      <LightModeOutlinedIcon
        fontSize="small"
        sx={(t) => ({
          fontSize: 16,
          color: isDark ? t.palette.text.secondary : '#111827',
          transition: 'color 200ms ease',
          zIndex: 2,
        })}
      />
      {/* Moon (dark mode end) */}
      <DarkModeOutlinedIcon
        fontSize="small"
        sx={(t) => ({
          fontSize: 16,
          color: isDark ? '#111827' : t.palette.text.secondary,
          transition: 'color 200ms ease',
          zIndex: 2,
        })}
      />
    </Box>
  );
}
